import axios from 'axios';
import Cookies from "js-cookie";
import { formatDateYMD, getTodayFormatted } from '../utils/dateUtils';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const resolveCalendarDateParam = (date) => {
  if (!date) return getTodayFormatted();
  if (typeof date === 'string') return date;
  return formatDateYMD(date);
};

export const buildCalendarByRefForDateUrl = (calendarRefId, date) => {
  const dateParam = resolveCalendarDateParam(date);
  return `${BASE_URL}/api/calendar/by-ref/${calendarRefId}/for-date?date=${encodeURIComponent(dateParam)}`;
};

export const fetchCalendarByRefForDate = async (calendarRefId, date) => {
  const response = await axios.get(buildCalendarByRefForDateUrl(calendarRefId, date));
  return response.data;
};

// -----------------------------
// LOCATIONS
// -----------------------------

// Fetch shipper locations using office codes (your MT API)
export const fetchLocationsByOfficeCodes = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/api/locations/by-office-codes`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch locations via office codes:", error);
    throw error;
  }
};

// Fetch full location object by locationRef
export const fetchLocationByRef = async (ref) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/locations/by-loc-ref/${ref}`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch location by ref:", error);
    throw error;
  }
};

// Fetch warehouses for a location (children locations)
export const fetchWarehousesByLocationId = async (locationId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/locations/children/by-id?parentId=${locationId}`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch warehouses:", error);
    throw error;
  }
};

// -----------------------------
// DOCKS
// -----------------------------

export const fetchDocksForLocationRef = async (locationRef) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/dock/docks/${locationRef}`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch docks:", error);
    throw error;
  }
};

export const fetchDockById = async (dockId) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/dock/${dockId}`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch dock by ID:", error);
    throw error;
  }
};

export const updateDock = async (dockId, payload) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/api/dock/${dockId}/update`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to update dock:", error);
    throw error;
  }
};

export const deleteDock = async (dockId) => {
  try {
    await axios.delete(`${BASE_URL}/api/dock/${dockId}`);
  } catch (error) {
    console.error("❌ Failed to delete dock:", error);
    throw error;
  }
};

// -----------------------------
// APPOINTMENTS
// -----------------------------

// Fetch all dock slots for a warehouse (new unified endpoint)
export const fetchWarehouseSlotsAvailability = async (locationRefId, date) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/slots/warehouse-availability`,
      {
        params: {
          loc_id_ref: locationRefId,
          date,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch warehouse slot availability:", error);
    throw error;
  }
};

// Fetch appointments for Shipper by locationRef (optionally filtered by date window)
export const fetchAppointmentsByLocationRef = async (locationRef, officeCode, range) => {
  const params =
    range?.start && range?.end
      ? { start: range.start, end: range.end }
      : undefined;

  try {
    const response = await axios.get(
      `${BASE_URL}/api/appointment/by-location`,
      {
        headers: {
          Location: locationRef,
          "Office-Code": officeCode,
        },
        params,
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch appointments by location:", error);
    throw error;
  }
};

// Fetch appointment by ID
export const fetchAppointmentById = async (appointmentId) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/appointment/${appointmentId}`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch appointment by ID:", error);
    throw error;
  }
};

// Cancel appointment
export const cancelAppointment = async (appointmentId) => {
  try {
    const token = Cookies.get("authToken");
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
    await axios.patch(`${BASE_URL}/api/appointment/${appointmentId}/cancel`, null, {
        headers: headers
    });
  } catch (error) {
    console.error("❌ Failed to cancel appointment:", error);
    throw error;
  }
};

// Fetch appointments by carrier office code
export const fetchAppointmentsByCarrier = async (officeCodeCarrier, range) => {
  const params =
    range?.start && range?.end
      ? { start: range.start, end: range.end }
      : undefined;

  try {
    const response = await axios.get(
      `${BASE_URL}/api/appointment/by-carrier`,
      {
        headers: {
          "Office-Code-Carrier": officeCodeCarrier,
        },
        params,
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch carrier appointments:", error);
    throw error;
  }
};


// -----------------------------
// EVENTS
// -----------------------------
export const fetchEventsByCalendarId = async (calendarId) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/events/by-calendar-id/${calendarId}`
  );
  return response.data;
};

// -----------------------------
// CALENDAR
// -----------------------------

export const fetchEventsByCalendarRefId = async (calendarRefId, date) => {
  return fetchCalendarByRefForDate(calendarRefId, date);
};

// -----------------------------
// EXCEPTION EVENTS
// -----------------------------

export const deleteExceptionEvent = async (exceptionId) => {
  await axios.delete(`${BASE_URL}/api/exception-events/${exceptionId}`);
};

export const reopenExceptionEvent = async (exceptionId, payload) => {
  const response = await axios.post(
    `${BASE_URL}/api/exception-events/${exceptionId}/reopen`,
    payload
  );
  return response.data;
};
  
// -----------------------------
// BULK CLOSURES
// -----------------------------

export const bulkCloseDocks = async (batch, officeCode) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/appointments/closures/batch`,
      batch,
      {
        headers: {
          "Office-Code": officeCode,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to perform bulk close:", error);
    throw error;
  }
};

export const rescheduleAppointment = async (appointmentId, payload, officeCode) => {
  try {
    const response = await axios.patch(
      `${BASE_URL}/api/appointment/${appointmentId}/reschedule`,
      payload,
      {
        headers: officeCode ? { "Office-Code": officeCode } : undefined,
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to reschedule appointment:", error);
    throw error;
  }
};

// -----------------------------
// SHIPMENTS
// -----------------------------

export const getShipmentByShipmentNum = async (shipmentNumber, officeCode) => {
  const token = Cookies.get("authToken");
  
  if (!shipmentNumber || !officeCode) {
    console.warn("⚠️ Missing shipmentNumber or officeCode");
    return null;
  }
  
  try {
    const response = await axios.get(
      `${BASE_URL}/api/shipments/detailed-shipment/${shipmentNumber}`, 
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Office-Code": officeCode
        }
      }
    );
  
    return response.data;
  } catch (error) {
    console.error(`❌ Failed to fetch shipment ${shipmentNumber}:`, error);
    return null;
  }
};

// -----------------------------
// CALENDAR COPY
// -----------------------------

export const applyWarehouseSettingsToDocks = async (payload) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/calendar-setup/apply-to-docks`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to apply warehouse settings to docks:", error);
    throw error;
  }
};
